import React, { useState } from "react";
import Breadcrumbs from "../../../utils/components/Breadcrumbs";
import Button from "../../../utils/components/Button";
import Table from "../../../utils/components/Table";
import Modal from "../../../utils/components/Modal";
import UploadFileButton from "../../../utils/components/UploadFileButton/UploadFileButton";
import TablePagination from "../../../utils/components/TablePagination";
import keepOrganizer from "../../../utils/functions/keepOrganizer";
import FinancialModal from "./components/FinancialModal";
import TransactionsSummary from "./components/TransactionsSummary";
import LoadingOverlay from "../../../utils/components/LoadingOverlay";

const FinancialPanelView = ({
    revaMe,
    revaAccounts,
    tournaments,
    priceLists,
    organizerId,
    debtSummary,
    selectedLineupInvoices,
    selectedLineupFinancials,
    loadLineupFinancials,
    loadPlayerFinancials,
    loadPriceList,
    applyPriceItem,
    uploadRevaPayment,
    confirmRevaPayment,
    registerInvoicesPayment,
    registerAddBalance,
    loadingReva,
    loadingTournaments,
    siloBalances,
    selectedLineupSiloBalances,
    incomeSummary,
    transactionsSummary,
    // New Props for Origin Tracking
    matches,
    sanctions,
    loadMatches,
    loadSanctions,
    createDebt,
    createPlayerDebt,
    revaConnectionError,
    registerPlayerInvoicesPayment,
    registerPlayerAddBalance,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);

    // Search and Filter State
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedTournamentId, setSelectedTournamentId] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [showZeroBalance, setShowZeroBalance] = useState(true);
    const [listTab, setListTab] = useState("entity"); // 'entity' | 'user'
    const [mainTab, setMainTab] = useState("general"); // 'general' | 'transactions'

    const formatBalance = (balance) => {
        if (typeof balance === "object" && balance !== null) {
            return Number(balance.amount) || 0;
        }
        return Number(balance) || 0;
    };

    const formatCurrency = (value) => {
        const number = formatBalance(value);
        return number.toLocaleString("de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const handleOpenFinancials = (account) => {
        setSelectedAccount(account);
        console.log('account', account);
        if (account.type === 'entity') {

            const id = account.accountable?.external_entity_id || account.external_entity_id || account.id;
            const tournamentId = account.tournament_id || account.accountable?.tournament_id;
            loadLineupFinancials(id);
            if (tournamentId && !priceLists[tournamentId]) loadPriceList(tournamentId);
        }

        if (account.type === 'user') {
            console.log('type is user');
            const id = account.target_id;
            const tournamentId = account.tournament_id;
            const lineupId = account.external_entity_id;
            loadPlayerFinancials(lineupId, id);
            if (tournamentId && !priceLists[tournamentId]) loadPriceList(tournamentId);
        }
        setShowModal(true);
    };

    const handleUploadPayment = (file) => {
        const formData = new FormData();
        formData.append("proof", file);
        const id = selectedAccount.accountable?.external_entity_id || selectedAccount.external_entity_id || selectedAccount.id;
        uploadRevaPayment(id, formData);
    };

    const handleConfirmPaymentWithoutProof = () => {
        const id = selectedAccount.accountable?.external_entity_id || selectedAccount.external_entity_id || selectedAccount.id;
        confirmRevaPayment(id);
    };

    // Filter and Sort Logic
    const mainAccount = (revaAccounts || []).find(acc => acc.name === (revaMe?.name || "Main Account")) || (revaAccounts || [])[0];

    // Build lookup maps from entity-type siloBalances:
    // - lineupToTournamentMap: lineup external_entity_id -> tournament_id
    // - lineupNameMap: lineup external_entity_id -> lineup/team name (target_name)
    const lineupToTournamentMap = {};
    const lineupNameMap = {};
    (siloBalances || [])
        .filter(acc => acc.type === 'entity' && acc.external_entity_id)
        .forEach(acc => {
            if (acc.external_relationship_id) {
                lineupToTournamentMap[acc.external_entity_id] = acc.external_relationship_id;
            }
            lineupNameMap[acc.external_entity_id] = acc.target_name;
        });

    // Build filtered accounts from siloBalances for the "Saldo por equipos/jugadores" list
    let filteredAccounts = (siloBalances || [])
        .filter(acc => acc.type === listTab)
        .map(acc => {
            // For 'entity' type: external_relationship_id is the tournament ID directly
            // For 'individual' type: external_entity_id is a lineup ID, look up the tournament via the lineup
            let tournamentId;
            let lineupName;
            if (acc.type === 'entity') {
                tournamentId = acc.external_relationship_id;
            } else {
                // individual: external_entity_id is a lineup ID, resolve tournament and lineup name from the entity map
                tournamentId = lineupToTournamentMap[acc.external_entity_id];
                lineupName = lineupNameMap[acc.external_entity_id];
            }

            return {
                ...acc,
                id: acc.account_id,
                name: acc.target_name,
                external_entity_id: acc.external_entity_id,
                tournament_id: tournamentId,
                tournament_name: tournaments.find(t => t.id === tournamentId)?.name || "N/A",
                lineup_name: lineupName || null,
                view_balance: {
                    available_balance: acc.balance?.available || 0,
                    pending_balance: acc.balance?.pending || 0
                }
            };
        });

    // Apply Tournament Filter
    if (selectedTournamentId) {
        filteredAccounts = filteredAccounts.filter(acc => acc.tournament_id === selectedTournamentId);
    }

    // Apply Search Term
    if (searchTerm) {
        const lowerSearch = searchTerm.toLowerCase();
        filteredAccounts = filteredAccounts.filter(acc =>
            acc.name.toLowerCase().includes(lowerSearch) ||
            acc.tournament_name?.toLowerCase().includes(lowerSearch)
        );
    }

    // Only show accounts with balance as per screenshot (default: != 0)
    if (!showZeroBalance) {
        filteredAccounts = filteredAccounts.filter(acc => formatBalance(acc.view_balance?.available_balance) !== 0);
    }

    // Apply Sorting (Amount Ascending - biggest debt first)
    filteredAccounts.sort((a, b) => {
        const balanceA = formatBalance(a.view_balance?.available_balance);
        const balanceB = formatBalance(b.view_balance?.available_balance);
        return balanceA - balanceB;
    });

    // Tournament Summary (totals of filtered/selected tournament)
    const totalDebt = filteredAccounts.reduce((sum, acc) => {
        const balance = formatBalance(acc.view_balance?.available_balance);
        return sum + (balance < 0 ? Math.abs(balance) : 0);
    }, 0);
    const totalOtherBalancePending = filteredAccounts.reduce((sum, acc) => sum + formatBalance(acc.view_balance?.pending_balance), 0);

    // Pagination
    const totalItems = filteredAccounts.length;
    const paginatedAccounts = filteredAccounts.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

    const rows = paginatedAccounts.map((acc) => ({
        ...acc,
        tournament: acc.tournament_name || acc.accountable?.tournament_name || "N/A",
        available: `$${formatCurrency(acc.view_balance?.available_balance)}`,
        pending: `$${formatCurrency(acc.view_balance?.pending_balance)}`,
        actions: (
            <Button
                className="btn btn-xs btn-success"
                title="Ver panel"
                onClick={() => handleOpenFinancials(acc)}
            />
        )
    }));

    return (
        <>
            <Breadcrumbs
                title="Panel Financiero"
                opts={[
                    { title: "Inicio", path: keepOrganizer("/") },
                    { title: "Panel Financiero", active: true },
                ]}
                rightHeader={
                    <Button
                        className="btn btn-primary"
                        title="Gestionar Lista de Precios"
                        onClick={() => window.location.href = keepOrganizer("/financial/prices")}
                    />
                }
            />

            <div className="wrapper wrapper-content">
                {revaConnectionError ? (
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="ibox m-t">
                                <div className="ibox-content">
                                    <div className="alert alert-danger" style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                                        <i className="fa fa-exclamation-triangle fa-2x m-r-md"></i>
                                        <div>
                                            <strong>Error de Conexión:</strong> La API Key de Reva parece ser inválida o ha expirado.
                                            Por favor, verifica la clave introducida en la sección de Integraciones. El Panel Financiero estará deshabilitado hasta que se resuelva.
                                        </div>
                                    </div>
                                    <div className="m-t-md text-center">
                                        <Button
                                            className="btn btn-primary"
                                            title="Ir a Integraciones"
                                            onClick={() => window.location.href = keepOrganizer("/integrations")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="tabs-container m-b-md">
                                <ul className="nav nav-tabs">
                                    <li className={mainTab === "general" ? "active" : ""}>
                                        <a onClick={() => setMainTab("general")} style={{ cursor: "pointer" }}>General</a>
                                    </li>
                                    <li className={mainTab === "transactions" ? "active" : ""}>
                                        <a onClick={() => setMainTab("transactions")} style={{ cursor: "pointer" }}>Transacciones</a>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {mainTab === "transactions" ? (
                            <div className="col-lg-12">
                                <TransactionsSummary transactions={transactionsSummary} loading={loadingReva.loadTransactionsSummary} />
                            </div>
                        ) : (
                            <>
                                {/* Left Column: Resumen General */}
                                <div className="col-md-5">
                                    <LoadingOverlay loading={loadingReva.loadIncomeSummary}>
                                    <div className="ibox no-margins">
                                        <div className="ibox-content p-md" style={{
                                            backgroundColor: '#1ab394',
                                            color: 'white',
                                            borderRadius: '15px 15px 0 0',
                                            border: 'none',
                                            padding: '20px 25px 30px 25px'
                                        }}>
                                            <h4 className="no-margins" style={{ textAlign: 'left', fontWeight: '600' }}>Resumen financiero</h4>
                                            <div className="row text-center" style={{ marginTop: '20px' }}>
                                                <div className="col-xs-6" style={{ borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                                                    <span style={{ opacity: 0.9, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Total cobrado</span>
                                                    <h2 className="no-margins" style={{ fontWeight: 'bold' }}>
                                                        ${formatCurrency(incomeSummary?.overview?.total_collected || 0)}
                                                    </h2>
                                                </div>
                                                <div className="col-xs-6">
                                                    <span style={{ opacity: 0.9, fontSize: '12px', display: 'block', marginBottom: '5px' }}>Total pendiente</span>
                                                    <h2 className="no-margins" style={{ fontWeight: 'bold' }}>
                                                        ${formatCurrency(incomeSummary?.overview?.total_pending || 0)}
                                                    </h2>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="ibox-content" style={{
                                            borderRadius: '0 0 15px 15px',
                                            border: 'none',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            marginTop: '-15px',
                                            position: 'relative',
                                            zIndex: 1,
                                            padding: '20px'
                                        }}>
                                            <div className="tabs-container">
                                                <ul className="nav nav-tabs" style={{ border: 'none', marginBottom: '15px' }}>
                                                    <li className="active"><a data-toggle="tab" href="#tab-itemized" style={{ padding: '8px 12px', border: 'none', background: 'transparent' }}>Por ítem</a></li>
                                                    <li><a data-toggle="tab" href="#tab-tournaments" style={{ padding: '8px 12px', border: 'none', background: 'transparent' }}>Por torneo</a></li>
                                                </ul>
                                                <div className="tab-content">
                                                    <div id="tab-itemized" className="tab-pane active">
                                                        <ul className="list-group clear-list m-t-sm">
                                                            {Object.entries(incomeSummary?.itemized_by_type || {}).map(([type, data], idx) => {
                                                                const mapping = {
                                                                    'match-fee': { label: "Derecho a partido", icon: "fa-flag" },
                                                                    'tournament-entry-fee': { label: "Inscripción", icon: "fa-file-text-o" },
                                                                    'sanction-fee': { label: "Sanción", icon: "fa-gavel" },
                                                                    'yellow-card-fee': { label: "Tarjeta amarilla", icon: "fa-square", iconColor: '#f1c40f' },
                                                                    'red-card-fee': { label: "Tarjeta roja", icon: "fa-square", iconColor: "#ed5565" },
                                                                    'double-yellow-card-fee': { label: "Doble amarilla", icon: "fa-square", iconColor: "#e67e22" },
                                                                    'protest-fee': { label: "Protesta", icon: "fa-bullhorn" },
                                                                    'social-club-fee': { label: "Club social", icon: "fa-university" },
                                                                    'custom-fee': { label: "Tipo personalizado", icon: "fa-cog" },
                                                                    'Add Balance': { label: "Carga de Saldo", icon: "fa-plus-circle", iconColor: "#23c6c8" },
                                                                };
                                                                const cat = mapping[type] || { label: type, icon: "fa-ellipsis-h" };

                                                                return (
                                                                    <li key={idx} className="list-group-item" style={{ border: 'none', padding: '10px 0', borderBottom: '1px solid #f3f3f4' }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                <span style={{ width: '25px', textAlign: 'center' }}>
                                                                                    <i className={`fa ${cat.icon}`} style={{ color: cat.iconColor || '#bdc3c7', fontSize: '15px' }}></i>
                                                                                </span>
                                                                                <span className="m-l-xs" style={{ fontSize: '14px', color: '#676a6c', fontWeight: '500' }}>{cat.label}</span>
                                                                            </div>
                                                                            <div style={{ textAlign: 'right' }}>
                                                                                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1ab394' }}>+ ${formatCurrency(data.collected)}</div>
                                                                                <div style={{ fontSize: '11px', color: '#ed5565' }}>Pendiente: ${formatCurrency(data.pending)}</div>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                );
                                                            })}
                                                            {!Object.keys(incomeSummary?.itemized_by_type || {}).length && (
                                                                <li className="list-group-item text-center text-muted" style={{ border: 'none' }}>No hay datos disponibles</li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                    <div id="tab-tournaments" className="tab-pane">
                                                        <ul className="list-group clear-list m-t-sm">
                                                            {Object.entries(incomeSummary?.by_relationship || {}).map(([relId, data], idx) => {
                                                                const tourney = tournaments.find(t => t.id === relId);
                                                                return (
                                                                    <li key={idx} className="list-group-item" style={{ border: 'none', padding: '10px 0', borderBottom: '1px solid #f3f3f4' }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                <span style={{ width: '25px', textAlign: 'center' }}>
                                                                                    <i className="fa fa-trophy" style={{ color: '#f8ac59', fontSize: '15px' }}></i>
                                                                                </span>
                                                                                <span className="m-l-xs" style={{ fontSize: '14px', color: '#676a6c', fontWeight: '500' }}>{tourney?.name || relId}</span>
                                                                            </div>
                                                                            <div style={{ textAlign: 'right' }}>
                                                                                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1ab394' }}>+ ${formatCurrency(data.collected)}</div>
                                                                                <div style={{ fontSize: '11px', color: '#ed5565' }}>Pendiente: ${formatCurrency(data.pending)}</div>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                );
                                                            })}
                                                            {!Object.keys(incomeSummary?.by_relationship || {}).length && (
                                                                <li className="list-group-item text-center text-muted" style={{ border: 'none' }}>No hay datos disponibles</li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    </LoadingOverlay>
                                </div>

                                {/* Right Column: Saldo por Equipos / Jugadores */}
                                <div className="col-md-7">
                                    <LoadingOverlay loading={loadingReva.loadSiloBalances}>
                                    <div className="ibox float-e-margins" style={{
                                        border: 'none',
                                        borderRadius: '15px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        overflow: 'hidden'
                                    }}>
                                        <div className="ibox-title" style={{ border: 'none', paddingBottom: '0', display: 'block', padding: '20px 25px 0 25px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <h5 style={{ fontSize: '18px', margin: 0 }}>Saldo por {listTab === 'entity' ? 'equipos' : 'jugadores'}</h5>
                                                <div className="btn-group">
                                                    <button
                                                        className={`btn btn-xs ${listTab === 'entity' ? 'btn-primary' : 'btn-white'}`}
                                                        onClick={() => { setListTab('entity'); setCurrentPage(0); }}
                                                    >
                                                        Equipos
                                                    </button>
                                                    <button
                                                        className={`btn btn-xs ${listTab === 'user' ? 'btn-primary' : 'btn-white'}`}
                                                        onClick={() => { setListTab('user'); setCurrentPage(0); }}
                                                    >
                                                        Jugadores
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="text-muted" style={{ fontSize: '12px', marginTop: '5px' }}>
                                                {showZeroBalance ? `Viendo todos los ${listTab === 'entity' ? 'equipos' : 'jugadores'}` : `Sólo muestran ${listTab === 'entity' ? 'equipos' : 'jugadores'} con saldo`}
                                            </div>
                                        </div>
                                        <div className="ibox-content" style={{ border: 'none', padding: '25px' }}>
                                            <div className="row m-b-md" style={{ display: 'flex', alignItems: 'center' }}>
                                                <div className="col-md-3">
                                                    <div className="checkbox checkbox-primary no-margins" style={{ display: 'flex', alignItems: 'center', marginTop: '0' }}>
                                                        <input
                                                            id="showZeroBalance"
                                                            type="checkbox"
                                                            checked={showZeroBalance}
                                                            onChange={(e) => setShowZeroBalance(e.target.checked)}
                                                            style={{ marginTop: '0', position: 'relative', margin: '0 5px 0 0' }}
                                                        />
                                                        <label htmlFor="showZeroBalance" style={{ fontSize: '11px', fontWeight: 'normal', cursor: 'pointer', userSelect: 'none', marginLeft: '0', paddingLeft: '0', marginBottom: '0', display: 'flex', alignItems: 'center' }}>
                                                            Ver {listTab === 'entity' ? 'equipos' : 'jugadores'} con saldo 0
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="col-md-5">
                                                    <div className="input-group">
                                                        <span className="input-group-addon" style={{ backgroundColor: '#f3f3f4', border: 'none' }}>
                                                            <i className="fa fa-search text-muted"></i>
                                                        </span>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Buscar..."
                                                            value={searchTerm}
                                                            style={{ backgroundColor: '#f3f3f4', border: 'none', boxShadow: 'none' }}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <select
                                                        className="form-control"
                                                        value={selectedTournamentId}
                                                        style={{ backgroundColor: '#f3f3f4', border: 'none', boxShadow: 'none' }}
                                                        onChange={(e) => {
                                                            setSelectedTournamentId(e.target.value);
                                                            setCurrentPage(0);
                                                        }}
                                                    >
                                                        <option value="">Todos los torneos</option>
                                                        {(tournaments || []).map(t => (
                                                            <option key={t.id} value={t.id}>{t.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="team-list" style={{ maxHeight: '700px', overflowY: 'auto' }}>
                                                {paginatedAccounts.map((acc, idx) => {
                                                    const balance = formatBalance(acc.view_balance?.available_balance);
                                                    const displayBalance = balance < 0 ? Math.abs(balance) : 0;

                                                    return (
                                                        <div
                                                            key={acc.id}
                                                            className="team-item pointer p-md"
                                                            onClick={() => handleOpenFinancials(acc)}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                borderBottom: '1px solid #f3f3f4',
                                                                transition: 'background 0.2s'
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
                                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <div className="m-r-md" style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <i className={`fa ${listTab === 'entity' ? 'fa-shield' : 'fa-user'} text-muted`}></i>
                                                                </div>
                                                                <div>
                                                                    <h4 className="no-margins" style={{ fontWeight: 'bold' }}>{acc.name}</h4>
                                                                    <small className="text-muted">{acc.type === 'individual' && acc.lineup_name ? `${acc.lineup_name} - ${acc.tournament_name || "N/A"}` : (acc.tournament_name || "N/A")}</small>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <h3 className="no-margins" style={{
                                                                    fontWeight: 'bold',
                                                                    color: balance === 0 ? '#333' : (balance < 0 ? '#ed5565' : '#1ab394')
                                                                }}>
                                                                    {balance > 0 ? '+' : ''}{formatCurrency(balance)}
                                                                </h3>
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {paginatedAccounts.length === 0 && (
                                                    <div className="text-center p-lg">
                                                        <i className="fa fa-search fa-3x text-muted m-b-md"></i>
                                                        <p className="text-muted">No se encontraron {listTab === 'entity' ? 'equipos' : 'jugadores'} con saldo.</p>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="m-t-md text-center">
                                                <TablePagination
                                                    defaultPage={currentPage}
                                                    defaultPageSize={pageSize}
                                                    totalItems={totalItems}
                                                    itemsInPage={paginatedAccounts.length}
                                                    onChangePage={(p) => setCurrentPage(p)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    </LoadingOverlay>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <FinancialModal
                show={showModal}
                onClose={() => setShowModal(false)}
                account={selectedAccount}
                organizerId={organizerId}
                financials={selectedLineupFinancials}
                invoices={selectedLineupInvoices}
                siloBalances={selectedLineupSiloBalances}
                priceList={priceLists[selectedAccount?.tournament_id || selectedAccount?.accountable?.tournament_id]}
                loading={loadingReva}
                applyPriceItem={applyPriceItem}
                registerInvoicesPayment={registerInvoicesPayment}
                registerAddBalance={registerAddBalance}
                // New Props
                matches={matches}
                sanctions={sanctions}
                loadMatches={loadMatches}
                loadSanctions={loadSanctions}
                createDebt={createDebt}
                createPlayerDebt={createPlayerDebt}
                registerPlayerInvoicesPayment={registerPlayerInvoicesPayment}
                registerPlayerAddBalance={registerPlayerAddBalance}
            />
        </>
    );
};

export default FinancialPanelView;
